<script setup>
import CtaBanner from '@/views/front-pages/landing-page/CtaBanner.vue'
import PageHero from '@/views/front-pages/PageHero.vue'
import PublicPageShell from '@/views/front-pages/PublicPageShell.vue'
import { useApi } from '@/utils/api'
import { useConfigStore } from '@core/stores/config'

definePage({ meta: { layout: 'blank', public: true } })

const store = useConfigStore()

store.skin = 'default'

// Addendum 15, Modul BR: data sungguhan dari GET /api/public/blog --
// endpoint itu sendiri HANYA mengembalikan artikel status=published
// (difilter di level query backend, lihat Public\BlogController), jadi
// tidak ada penyaringan tambahan yang perlu dilakukan di sini.
const posts = ref([])
const loading = ref(true)

const fetchPosts = async () => {
  loading.value = true
  try {
    const response = await useApi('/api/public/blog').get()
    const body = response?.data ?? response

    posts.value = Array.isArray(body) ? body : (body?.data ?? [])
  } catch (err) {
    console.error('❌ Gagal memuat daftar artikel:', err)
    posts.value = []
  } finally {
    loading.value = false
  }
}

onMounted(fetchPosts)

const formatDate = value => {
  if (!value) return ''

  return new Date(value).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })
}
</script>

<template>
  <PublicPageShell>
    <PageHero
      eyebrow="Blog"
      eyebrow-icon="ri-article-line"
      title="Blog PintuKasir"
      subtitle="Tips, panduan, dan insight seputar operasional coffee shop & UMKM F&B Indonesia."
    />

    <section class="blog-list-section pb-16">
      <VContainer>
        <div
          v-if="loading"
          class="text-center py-12"
        >
          <VProgressCircular
            indeterminate
            color="primary"
          />
        </div>

        <div
          v-else-if="posts.length === 0"
          class="text-center py-12"
        >
          <VIcon
            icon="ri-quill-pen-line"
            size="56"
            class="text-medium-emphasis mb-4"
          />
          <h2 class="text-h5 font-weight-bold mb-2">
            Belum Ada Artikel
          </h2>
          <p
            class="text-medium-emphasis mx-auto"
            style="max-width: 480px"
          >
            Kami sedang menyiapkan artikel-artikel bermanfaat seputar bisnis F&B dan UMKM. Nantikan update selanjutnya di sini.
          </p>
        </div>

        <VRow v-else>
          <VCol
            v-for="post in posts"
            :key="post.slug"
            cols="12"
            sm="6"
            md="4"
          >
            <VCard
              class="blog-card h-100"
              rounded="lg"
              variant="outlined"
              :to="{ name: 'blog-slug', params: { slug: post.slug } }"
            >
              <VImg
                v-if="post.featured_image_url"
                :src="post.featured_image_url"
                height="180"
                cover
              />
              <div
                v-else
                class="blog-card-placeholder d-flex align-center justify-center"
              >
                <VIcon
                  icon="ri-article-line"
                  size="40"
                  color="primary"
                />
              </div>

              <VCardText class="pa-5">
                <div class="text-caption text-medium-emphasis mb-2">
                  {{ formatDate(post.published_at) }}
                </div>
                <h3 class="text-h6 font-weight-bold mb-2">
                  {{ post.title }}
                </h3>
                <p
                  v-if="post.excerpt"
                  class="text-body-2 text-medium-emphasis mb-0"
                  style="line-height: 1.6"
                >
                  {{ post.excerpt }}
                </p>
              </VCardText>
            </VCard>
          </VCol>
        </VRow>
      </VContainer>
    </section>

    <CtaBanner />
  </PublicPageShell>
</template>

<style lang="scss" scoped>
.blog-list-section {
  background: rgba(var(--v-theme-surface), 1);
}

.blog-card {
  text-decoration: none;
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    box-shadow: 0 8px 24px rgba(var(--v-shadow-key-umbra-color), 12%) !important;
    transform: translateY(-2px);
  }
}

.blog-card-placeholder {
  block-size: 180px;
  background: rgba(var(--v-theme-primary), 0.08);
}
</style>
